import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
    const { user, logout } = useAuth();
    const { t, i18n } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    
    // --- RESPONSIVE STATE ---
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const changeLanguage = (lng) => i18n.changeLanguage(lng);
    const currentLanguage = i18n.language;
    const isStaff = user && (user.role === 'hotel' || user.role === 'admin');

    // --- SHARED STYLES ---
    const navLinkBase = {
        textDecoration: 'none',
        fontSize: isMobile ? '11px' : '13px',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '4px 6px' : '6px 10px',
        borderRadius: '8px'
    };

    const langBtnStyle = (active) => ({
        border: 'none',
        padding: isMobile ? '3px 8px' : '4px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '10px',
        fontWeight: '700',
        backgroundColor: active ? '#ffffff' : 'transparent',
        color: active ? '#1e90ff' : '#94a3b8',
        boxShadow: active ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
        transition: 'all 0.2s'
    });

    return (
        <nav style={{ 
            minHeight: isMobile ? 'auto' : '60px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #f1f5f9',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            // --- FIX: Dynamic padding based on device and role ---
            paddingLeft: isMobile ? '15px' : (isStaff ? '280px' : '30px'), 
            paddingRight: isMobile ? '15px' : '30px',
            paddingTop: isMobile ? '10px' : '0px',
            paddingBottom: isMobile ? '10px' : '0px',
            boxSizing: 'border-box',
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)'
        }}>
            <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', // Stack on mobile
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                width: '100%',
                margin: '0 auto',
                gap: isMobile ? '10px' : '0px'
            }}>
                
                {/* --- SECTION 1: BRANDING --- */}
                <Link 
                    to="/" 
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        textDecoration: 'none',
                        gap: '10px',
                        opacity: isHovered ? 0.7 : 1,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div style={{ 
                        background: '#1e90ff', 
                        color: 'white', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        borderRadius: '6px', 
                        fontWeight: '900',
                        fontSize: '16px'
                    }}>
                        {isStaff ? '🏠' : 'É'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '800', fontSize: '14px', color: '#1e293b', lineHeight: 1 }}>
                            ÉPICURYS
                        </span>
                        {!isMobile && ( // Hide subtitle on small phones to save space
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', marginTop: '2px', letterSpacing: '0.5px' }}>
                                {isStaff ? 'CONTROL CENTER' : 'HOTEL PLATFORM'}
                            </span>
                        )}
                    </div>
                </Link>

                {/* --- SECTION 2: NAVIGATION & ACTIONS --- */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? '10px' : '30px',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'space-between'
                }}>
                    
                    {/* Guest Links */}
                    {!isStaff && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <Link to="/checkin" style={{ ...navLinkBase, color: '#64748b' }}>
                                {isMobile ? 'Check-In' : t('home.guest_checkin')}
                            </Link>
                            <Link to="/checkout" style={{ ...navLinkBase, color: '#64748b' }}>
                                {isMobile ? 'Check-Out' : t('home.guest_checkout')}
                            </Link>
                        </div>
                    )}

                    {/* Auth & Language Area */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: isMobile ? '8px' : '15px', 
                        borderLeft: isMobile ? 'none' : '1px solid #f1f5f9', 
                        paddingLeft: isMobile ? '0px' : '15px' 
                    }}>
                        {user ? (
                            <button 
                                onClick={logout} 
                                style={{ 
                                    background: '#fee2e2', 
                                    border: 'none', 
                                    color: '#ef4444', 
                                    fontWeight: '700', 
                                    fontSize: '10px', 
                                    cursor: 'pointer',
                                    padding: '6px 10px',
                                    borderRadius: '6px'
                                }}
                            >
                                {t('Logout').toUpperCase()}
                            </button>
                        ) : (
                            <Link 
                                to="/login" 
                                style={{ 
                                    ...navLinkBase, 
                                    backgroundColor: '#1e293b', 
                                    color: '#fff',
                                    padding: isMobile ? '6px 10px' : '6px 16px',
                                    fontSize: '10px'
                                }}
                            >
                                LOGIN
                            </Link>
                        )}

                        {/* Language Switcher */}
                        <div style={{ 
                            display: 'flex', 
                            background: '#f1f5f9', 
                            padding: '2px', 
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <button 
                                style={langBtnStyle(currentLanguage === 'en')} 
                                onClick={() => changeLanguage('en')}
                            >EN</button>
                            <button 
                                style={langBtnStyle(currentLanguage === 'fr')} 
                                onClick={() => changeLanguage('fr')}
                            >FR</button>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}